import {
  Component,
  inject,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterModule,
} from '@angular/router';
import {
  AlertService,
  ConnectionService,
  CrispService,
  GoogleTagManagerService,
  LanguageService,
  PingService,
} from '@itb-monorepo/services';
import { ModalComponent } from '@itb-monorepo/shared-ui/feedback';
import { ThemeMode, ThemeService } from '@itb-monorepo/shared-ui/theme';
import { LoadingBarRouterModule } from '@ngx-loading-bar/router';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { filter } from 'rxjs/operators';
import { TourOverlayComponent } from '@itb-monorepo/tour';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    RouterModule,
    LoadingBarRouterModule,
    ModalComponent,
    TranslateModule,
    TourOverlayComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class AppComponent implements OnInit {
  title = 'ITB Trader Panel';
  currentLang: string;
  currentTheme?: ThemeMode;
  margin = 0;
  isExternal = false;
  cache = 0;
  isRefresh = false;
  isDisconnected = false;
  private pingCheckId = 0;
  private consecutivePingFailures = 0;
  @ViewChild('connectionModal') connectionModal: ModalComponent;

  private alertService = inject(AlertService);
  private router = inject(Router);
  private pingService = inject(PingService);
  private tagManagerService = inject(GoogleTagManagerService);
  private crispService = inject(CrispService);
  private connectionService = inject(ConnectionService);
  private languageService = inject(LanguageService);
  private activateRoute = inject(ActivatedRoute);
  private themeService = inject(ThemeService);

  constructor() {
    this.cache = new Date().getTime();

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationStart | NavigationEnd | NavigationError =>
            event instanceof NavigationStart ||
            event instanceof NavigationEnd ||
            event instanceof NavigationError
        )
      )
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.languageService.syncLanguageFromUrl(event.url);
          return;
        }

        if (event instanceof NavigationError) {
          const message = event.error?.message as string;

          if (
            message?.includes('Failed to fetch dynamically imported module')
          ) {
            location.replace(event.url);
          }

          return;
        }
        if (!this.isRefresh) {
          const om = window['om355764_336110'];

          if (om) {
            om.reset();
          }
        }

        this.isRefresh = false;

        this.isExternal =
          this.activateRoute.root.firstChild?.snapshot.data['external'];

        if (event.urlAfterRedirects.startsWith('/auth')) {
          this.isExternal = true;
        }

        if (window['clarity']) {
          window['clarity']('set', 'route', event.urlAfterRedirects);
        }
      });

    this.connectionVPNStatus();
    this.checkConnection();

    this.languageService.getCurrentLanguage().subscribe((res: string) => {
      const om = window['om355764_336110'];

      if (om) {
        om.reset();
      }

      this.currentLang = res;
    });

    this.themeService.getThemeMode().subscribe((res: ThemeMode) => {
      this.currentTheme = res;
    });
  }

  ngOnInit() {
    const navigationEntries = performance.getEntriesByType('navigation');

    if (navigationEntries.length > 0) {
      const navigationType = (navigationEntries[0] as any).type;

      if (navigationType === 'reload') {
        this.isRefresh = true;
      } else if (navigationType === 'navigate') {
        const referrer = document.referrer;
        this.isRefresh = !referrer;
      }
    }

    setTimeout(() => {
      if (window['clarity']) {
        window['clarity']('set', 'page', this.router.url);
      }
    }, 0);
  }

  connectionVPNStatus() {
    setInterval(() => {
      this.checkConnection();
    }, 10000);
  }

  checkConnection(): void {
    const checkId = ++this.pingCheckId;
    let isCheckComplete = false;

    const t = setTimeout(() => {
      if (isCheckComplete || checkId !== this.pingCheckId) return;
      isCheckComplete = true;
      this.handlePingFailure();
    }, 8500);

    this.pingService.getBroadcastVPNStatus().subscribe({
      next: () => {
        if (isCheckComplete || checkId !== this.pingCheckId) return;
        isCheckComplete = true;
        clearTimeout(t);
        this.consecutivePingFailures = 0;
        this.isDisconnected = false;
        if (this.connectionModal) {
          this.connectionModal.closable = true;
          this.connectionModal.close();
        }
      },
      error: () => {
        if (isCheckComplete || checkId !== this.pingCheckId) return;
        isCheckComplete = true;
        clearTimeout(t);
        this.handlePingFailure();
      },
    });
  }
  private handlePingFailure() {
    this.consecutivePingFailures++;
    if (this.consecutivePingFailures < 10) return;

    this.isDisconnected = true;
    if (this.connectionModal) {
      this.connectionModal.closable = false;
      this.connectionModal.open();
    }
  }
}
